import React, { PropTypes } from 'react'
import { connect } from 'react-redux'
import { Field } from 'redux-form'

import { Input, Select, Label } from 'form'
import { titleOptionsSelector } from 'settings'
import SelectInstitution from 'select-institution'

const IntakeQuestionField = ({ question, titleOptions, ...props }) => {
  const { question_type: questionType, question_text: questionText, options, storage_path: storagePath, storage_table: storageTable, is_required: isRequired } = question
  const fieldName = `intake_answer_${question.id}`

  const fieldProps = {
    name: fieldName,
    ...props
  }

  const labelText = isRequired ? `${questionText} *` : questionText

  // Handle special case for university selection
  if (storagePath === 'university' && storageTable === 'users') {
    return (
      <div style={{ marginBottom: -18 }}>
        <Label htmlFor={fieldName}>{labelText}</Label>
        <Field {...fieldProps} component={SelectInstitution} />
      </div>
    )
  }

  // Handle special case for title selection
  if (storagePath === 'title' && storageTable === 'users') {
    return (
      <div>
        <Label htmlFor={fieldName}>{labelText}</Label>
        <Field {...fieldProps} component={Select} hideMeta options={titleOptions} />
      </div>
    )
  }

  switch (questionType) {
    case 'text':
      return (
        <div>
          <Label htmlFor={fieldName}>{labelText}</Label>
          <Field {...fieldProps} hideMeta component={Input} />
        </div>
      )

    case 'number':
      return (
        <div>
          <Label htmlFor={fieldName}>{labelText}</Label>
          <Field {...fieldProps} component={Input} hideMeta type='number' min={0} onWheel={e => e.target.blur()} />
        </div>
      )

    case 'select': {
      const selectOptions = [
        { value: '', name: 'Please select...' },
        ...(options || []).map(opt => ({
          value: opt.value,
          name: opt.name
        }))
      ]
      return (
        <div>
          <Label htmlFor={fieldName}>{labelText}</Label>
          <Field {...fieldProps} component={Select} hideMeta options={selectOptions} />
        </div>
      )
    }

    case 'checkbox': {
      // For checkbox questions, we need to handle multiple selections
      const CheckboxGroup = ({ input, options }) => (
        <div>
          {(options || []).map(option => (
            <div key={option.value}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type='checkbox'
                  checked={input.value && input.value.includes(option.value)}
                  onChange={(e) => {
                    const newValue = [...(input.value || [])]
                    if (e.target.checked) {
                      newValue.push(option.value)
                    } else {
                      const index = newValue.indexOf(option.value)
                      if (index > -1) {
                        newValue.splice(index, 1)
                      }
                    }
                    input.onChange(newValue)
                  }}
                  style={{ marginRight: 8 }}
                />
                <span style={{ fontSize: 14 }}>{option.name}</span>
              </label>
            </div>
          ))}
        </div>
      )
      return (
        <fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
          <legend style={{ fontWeight: 'bold', marginBottom: 4 }}>{labelText}</legend>
          <Field {...fieldProps} component={CheckboxGroup} options={options} />
        </fieldset>
      )
    }

    default:
      return (
        <div>
          <Label htmlFor={fieldName}>{labelText}</Label>
          <Field {...fieldProps} component={Input} />
        </div>
      )
  }
}

IntakeQuestionField.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.number.isRequired,
    question_text: PropTypes.string.isRequired,
    question_type: PropTypes.string.isRequired,
    options: PropTypes.array,
    storage_path: PropTypes.string,
    storage_table: PropTypes.string,
    is_required: PropTypes.bool
  }).isRequired,
  titleOptions: PropTypes.array
}

export const IntakeQuestionsForm = ({ intakeQuestions, titleOptions }) => {
  if (!intakeQuestions || intakeQuestions.length === 0) {
    return null
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {intakeQuestions.map(question => {
        return (
          <div key={question.id}>
            <IntakeQuestionField question={question} titleOptions={titleOptions} />
          </div>
        )
      })}
    </div>
  )
}

IntakeQuestionsForm.propTypes = {
  intakeQuestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      question_text: PropTypes.string.isRequired,
      question_type: PropTypes.string.isRequired,
      options: PropTypes.array,
      storage_path: PropTypes.string,
      storage_table: PropTypes.string,
      is_required: PropTypes.bool
    })
  ),
  titleOptions: PropTypes.array
}

const mapStateToProps = state => ({
  titleOptions: titleOptionsSelector(state)
})

export default connect(mapStateToProps)(IntakeQuestionsForm)
